export type FolderOrLeaf<Type> = Folder<Type> | Leaf<Type>;

export type Leaf<Type> = {
    name: string;
    value: Type;
};

export type Folder<Type> = {
    name: string;
    isExpanded: boolean;
    children: FolderOrLeaf<Type>[];
};

export function isLeaf<Type>(item: FolderOrLeaf<Type>): item is Leaf<Type> {
    return (item as Leaf<Type>).value !== undefined;
}

export function isFolder<Type>(item: FolderOrLeaf<Type>): item is Folder<Type> {
    return (item as Folder<Type>).children !== undefined;
}

function newLeaf<Type>(name: string, value: Type): Leaf<Type> {
    return { name, value };
}

function newFolder<Type>(name: string, children: FolderOrLeaf<Type>[]): Folder<Type> {
    return {
        name,
        children,
        isExpanded: false
    };
}

// Normal split() method not suitable, have to create my own
function splitOnFirstFolder(path: string): string[] {
    const index = path.indexOf('/');
    return index !== -1
        ? [path.substring(0, index), path.substring(index + 1)]
        : [path];
}

function itemToFolderOrLeaf<Type>(item: Type, path: string): FolderOrLeaf<Type> {
    const parts = splitOnFirstFolder(path);
    if (parts.length === 1) {
        return newLeaf(parts[0], item);
    } else {
        return newFolder(parts[0], [
            itemToFolderOrLeaf(item, parts[1])
        ]);
    }
}

function mergeFolders<Type>(items: FolderOrLeaf<Type>[]): FolderOrLeaf<Type>[] {
    items.sort((a, b) => {
        const aIsFolder = isFolder(a);
        const bIsFolder = isFolder(b);

        if (aIsFolder !== bIsFolder) {
            return aIsFolder ? -1 : 1;
        }

        return a.name.localeCompare(b.name);
    });

    const result: FolderOrLeaf<Type>[] = [];

    let latestFolderAdded: Folder<Type> | null = null;

    while (items.length > 0) {
        const current = items.shift()!;
        if (isLeaf(current)) {
            result.push(current);
        } else {
            if (latestFolderAdded?.name === current.name) {
                const currentChildren = (current as Folder<Type>).children;
                if (currentChildren.length > 0) {
                    latestFolderAdded.children.push(...currentChildren);
                }
            } else {
                latestFolderAdded = current as Folder<Type>;
                result.push(current);
            }
        }
    }

    // Decend into children
    result.filter(isFolder).forEach(folder => {
        if (folder.children.length > 1) {
            folder.children = mergeFolders(folder.children);
        }
    });

    return result;
}

/**
 * Trims leading and trailing forward slash (/).
 * Reduces duplicate forward slashes (/// => /).
 */
function normalizePath(path: string): string {
    return path
        .replace(/^\/+|\/+$/g, '')
        .replace(/\/\/+/g, '/');
}

export type PathFunction<Type> = (item: Type) => string;

/**
 * Builds a multi-root tree structure of folders and leafs from a list of objects and their paths.
 * The paths are produced by the provided PathFunction. A path is a string with slash separators
 * between folders, and with the leaf item at the end/right.
 *
 * Example path: "mainFolder/subFolder/someItem"
 */
export function buildFolderStructure<Type>(items: Type[], getPath: PathFunction<Type>): FolderOrLeaf<Type>[] {
    return mergeFolders(
        items.map(x =>
            itemToFolderOrLeaf(x, normalizePath(getPath(x)))));
}
